import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { StandardApiError } from '@helios/messaging'
import {
  HeliosHttpError,
  reportError,
  RootState,
} from '@helios/state-management'
import {
  ERROR_INTERCEPTOR_BYPASS,
  getToday,
  HANDLE_ERROR_AND_THROW,
  IGNORE_ERROR,
} from '@helios/utils'
import { Store } from '@ngrx/store'
import { EMPTY, Observable, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import NodeCache from 'node-cache'
import { IGNORE_SESSION_ID, SESSION_ID_HEADER } from './session.interceptor'
import { v4 as UUID } from 'uuid'

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly errorMessageCache = new NodeCache({
    forceString: false,
    stdTTL: 60, //1 Minute
    checkperiod: 60,
    errorOnMissing: false,
    useClones: false,
    deleteOnExpire: true,
  })

  constructor(private readonly store: Store<RootState>) {}

  static standardApiErrorToMessage(
    error: StandardApiError,
    defaultMessage: string | null,
  ): string {
    //TODO NFORK-2881 switch to using a template after this case is closed.
    const messages: string[] = [
      error.displayMessage ??
        error.errorMessage ??
        `Unknown API Error: ${defaultMessage}`,
    ]

    if (error.errorId != null) {
      messages.push(`ErrorId: ${error.errorId}`)
    }

    const message = messages.join('\n')
    //PVAL-8535 Logging for FullStory
    console.error(message)

    return message
  }

  intercept(
    httpRequest: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(httpRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        /**
         * We need to skip the standard error checking by
         * bypassing this and throwing the exception up the chain.
         *
         * Usage:
         *  http.get(url, { context: new HttpContext().set(ERROR_INTERCEPTOR_BYPASS, [403]) })
         */
        const bypass = httpRequest.context.get(ERROR_INTERCEPTOR_BYPASS) ?? []
        if (bypass.includes(err.status)) {
          return throwError(err)
        }

        const ignore = httpRequest.context.get(IGNORE_ERROR) ?? []
        if (ignore.includes(err.status)) {
          return EMPTY
        }

        this.handleStandardApiError(httpRequest, err)
        if (httpRequest.context.get(HANDLE_ERROR_AND_THROW)) {
          return throwError(err)
        }

        return EMPTY
      }),
    )
  }

  /**
   * @internal
   * @param error
   * @param url
   */
  private handleStandardApiError(
    httpRequest: HttpRequest<unknown>,
    err: HttpErrorResponse,
  ) {
    const apiError: StandardApiError = (err.error as StandardApiError) ?? {}
    const msg = ErrorInterceptor.standardApiErrorToMessage(apiError, err.url)

    /**
     * If the message has been reported in the last minute don't report it again.
     */
    if (this.errorMessageCache.has(msg)) {
      return
    }

    this.errorMessageCache.set(msg, true)

    let sessionId = httpRequest.headers.get(SESSION_ID_HEADER)
    if (sessionId === IGNORE_SESSION_ID) {
      sessionId = null
    }

    this.store.dispatch(
      reportError({
        id: UUID(),
        timestamp: getToday(),
        message: msg,
        type: 'http',
        sessionId: sessionId,
        error: JSON.stringify(err.error),
        url: err.url,
        responseCode: err.status,
        requestPayload: JSON.stringify(httpRequest.body),
      } as HeliosHttpError),
    )
  }
}
